import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "../../../components/layout/Navbar";
import { Footer } from "../../../components/layout/Footer";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { User, Mail, Lock, ArrowLeft } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { useAuth } from "../../../contexts/AuthContext";
import { useLanguage } from "../../../contexts/LanguageContext";

const AdminRegister = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const { registerAdmin } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { language } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Simple validation
    if (!formData.name || !formData.email || !formData.password) {
      toast({
        title: language === "ar" ? "خطأ" : "Error",
        description: language === "ar" ? "يرجى ملء جميع الحقول" : "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: language === "ar" ? "خطأ" : "Error",
        description: language === "ar" ? "كلمة المرور يجب أن تكون 6 أحرف على الأقل" : "Password must be at least 6 characters",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const success = await registerAdmin(formData);

      if (success) {
        toast({
          title: language === "ar" ? "تم إنشاء الحساب بنجاح" : "Account Created Successfully",
          description: language === "ar" ? "تم إنشاء حساب المدير بنجاح" : "Admin account created successfully",
        });
        navigate("/login");
      } else {
        throw new Error("Failed to create admin account");
      }
    } catch (error: any) {
      toast({
        title: language === "ar" ? "فشل إنشاء الحساب" : "Account Creation Failed",
        description: language === "ar" ? "فشل في إنشاء حساب المدير" : "Failed to create admin account",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" dir={language === "ar" ? "rtl" : "ltr"}>
      <Navbar />

      <main className="flex-1 py-12">
        <div className="container mx-auto px-4 max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {language === "ar" ? "العودة للرئيسية" : "Back to Home"}
          </Link>

          <Card className="border-border shadow-lg animate-fade-in">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">
                {language === "ar" ? "تسجيل مدير" : "Admin Registration"}
              </CardTitle>
              <CardDescription>
                {language === "ar" ? "إنشاء حساب مدير جديد" : "Create a new admin account"}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{language === "ar" ? "الاسم" : "Name"}</Label>
                  <div className="relative">
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder={language === "ar" ? "أدخل اسمك" : "Enter your name"}
                      value={formData.name}
                      onChange={handleInputChange}
                      className={language === "ar" ? "pr-10" : "pl-10"}
                      required
                    />
                    <User className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{language === "ar" ? "البريد الإلكتروني" : "Email"}</Label>
                  <div className="relative">
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="admin@knowvest.plus"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={language === "ar" ? "pr-10" : "pl-10"}
                      required
                    />
                    <Mail className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{language === "ar" ? "كلمة المرور" : "Password"}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={language === "ar" ? "pr-10" : "pl-10"}
                      required
                    />
                    <Lock className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={loading}
                >
                  {loading ?
                    (language === "ar" ? "جاري الإنشاء..." : "Creating...") :
                    (language === "ar" ? "إنشاء حساب المدير" : "Create Admin Account")}
                </Button>

                <div className="text-center pt-4">
                  <p className="text-muted-foreground">
                    {language === "ar" ? "لديك حساب؟" : "Already have an account?"}{" "}
                    <Link
                      to="/login"
                      className="text-primary hover:underline"
                    >
                      {language === "ar" ? "تسجيل الدخول" : "Login"}
                    </Link>
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminRegister;